import React, { useState } from "react";
import { Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { SERVICES } from "@/data/services";
import { CITIES } from "@/data/site";

// Form, kendi domain'imizdeki contact.php'a POST atar.
// contact.php arka planda Web3Forms API'ye istek atar.
const CONTACT_ENDPOINT = "/contact.php";
const HONEYPOT_FIELD = "website"; // contact.php'deki HONEYPOT_FIELD ile aynı olmalı

const initial = {
  adSoyad: "",
  firma: "",
  telefon: "",
  eposta: "",
  hizmet: "",
  sehir: "",
  mesaj: "",
};

const ERROR_MSG = "Mesajınız iletilemedi. Lütfen WhatsApp üzerinden iletişime geçin.";

export default function QuoteForm({ defaultService }) {
  const [form, setForm] = useState({ ...initial, hizmet: defaultService || "" });
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const payload = { ...form, [HONEYPOT_FIELD]: honeypot };
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.ok && data && data.ok === true) {
        // Başarı — formu temizle
        setStatus("success");
        setForm({ ...initial, hizmet: defaultService || "" });
        setHoneypot("");
        return;
      }

      // Hata — form değerlerini KORUYORUZ (kullanıcı tekrar denesin)
      const detail = (data && typeof data.error === "string" && data.error) ? data.error : ERROR_MSG;
      setError(detail);
      setStatus("error");
    } catch {
      setError(ERROR_MSG);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="quote-success"
        className="ind-card p-8 sm:p-10 border border-emerald-200 bg-emerald-50/50"
      >
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-4" />
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Talebiniz alındı</h3>
        <p className="text-slate-600 leading-relaxed">
          En kısa sürede sizinle iletişime geçeceğiz. Acil durumlar için WhatsApp
          veya telefon hattını kullanabilirsiniz.
        </p>
        <button
          onClick={() => setStatus("idle")}
          data-testid="quote-new-btn"
          className="btn-secondary mt-6 text-sm"
        >
          Yeni talep oluştur
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="ind-card p-6 sm:p-8 space-y-5"
    >
      {/* Honeypot — gerçek kullanıcılar göremez, botlar doldurursa istek reddedilir */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
      >
        <label htmlFor="dv-website">Web sitesi (boş bırakın)</label>
        <input
          id="dv-website"
          name={HONEYPOT_FIELD}
          type="text"
          tabIndex="-1"
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Ad Soyad *">
          <input
            required
            type="text"
            name="adSoyad"
            value={form.adSoyad}
            onChange={update("adSoyad")}
            data-testid="quote-input-full_name"
            className="form-input"
            placeholder="Adınız soyadınız"
          />
        </Field>
        <Field label="Firma">
          <input
            type="text"
            name="firma"
            value={form.firma}
            onChange={update("firma")}
            data-testid="quote-input-company"
            className="form-input"
            placeholder="Firma adı"
          />
        </Field>
        <Field label="Telefon *">
          <input
            required
            type="tel"
            name="telefon"
            value={form.telefon}
            onChange={update("telefon")}
            data-testid="quote-input-phone"
            className="form-input"
            placeholder="+90 5XX XXX XX XX"
          />
        </Field>
        <Field label="E-posta">
          <input
            type="email"
            name="eposta"
            value={form.eposta}
            onChange={update("eposta")}
            data-testid="quote-input-email"
            className="form-input"
            placeholder="ornek@firma.com"
          />
        </Field>
        <Field label="Hizmet *">
          <select
            required
            name="hizmet"
            value={form.hizmet}
            onChange={update("hizmet")}
            data-testid="quote-input-service"
            className="form-input"
          >
            <option value="">Seçiniz</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>
                {s.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Şehir *">
          <select
            required
            name="sehir"
            value={form.sehir}
            onChange={update("sehir")}
            data-testid="quote-input-city"
            className="form-input"
          >
            <option value="">Seçiniz</option>
            {CITIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Mesaj">
        <textarea
          rows={5}
          name="mesaj"
          value={form.mesaj}
          onChange={update("mesaj")}
          data-testid="quote-input-message"
          className="form-input resize-none"
          placeholder="Ekipman, problem ve beklentilerinizi kısaca yazınız."
        />
      </Field>

      {status === "error" && (
        <div
          data-testid="quote-error"
          className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 text-red-700 text-sm rounded-sm"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="quote-submit-btn"
          className="btn-primary disabled:opacity-60"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor...
            </>
          ) : (
            <>
              Talebimi Gönder <Send className="w-4 h-4" />
            </>
          )}
        </button>
        <p className="text-xs text-slate-500 self-center">
          Bilgileriniz yalnızca size dönüş yapmak için kullanılır.
        </p>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 12px 14px;
          background-color: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 2px;
          font-size: 14px;
          color: #0f172a;
          transition: border-color .2s, box-shadow .2s;
          font-family: 'IBM Plex Sans', sans-serif;
        }
        .form-input:focus {
          outline: none;
          border-color: #0891b2;
          box-shadow: 0 0 0 3px rgba(8,145,178,0.15);
        }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 uppercase tracking-[0.12em] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
